// src/components/instructor/OrgTeamSeasons.js — Pass 48 §5-8: the longitudinal Team
// directory. Season tabs [2026/27][2025/26][2024/25][All people] driven by the
// canonical people model (members + seasonMemberships + getMembersForSeason), NOT by
// buildFinanceData. One person appears once; School/Collective is season-specific;
// teaching vs other staff are distinguishable; capabilities shown separately.
import React, { useMemo, useState } from 'react';
import { getMembersForSeason, splitTeachingStaff } from '../../utils/seasonScope';
import { resolveInstructorCategory, resolveCurrentTeamState, TEAM_STATE, isOperationalStaff } from '../../utils/instructorCategory';
import { getCapabilities } from '../../utils/activityModel';

const V = { t1: '#1A1917', t2: '#6B6860', t3: '#9E9B95', border: '#E2E0DB', card: '#FFFFFF', surface: '#F2F1EE', blue: '#2563EB', purple: '#7C3AED', amber: '#C97D10', green: '#1D9E75' };
const F = "'DM Sans',sans-serif";
const SEASONS = [['2026_27', '2026/27'], ['2025_26', '2025/26'], ['2024_25', '2024/25'], ['all', 'All people']];
const catColor = (c) => (c === 'collective' ? V.purple : c === 'school' ? V.blue : V.amber);
const catLabel = (c) => (c === 'collective' ? 'Collective' : c === 'school' ? 'SAS School' : 'Unclassified');
const stateLabel = { [TEAM_STATE.CURRENT]: 'Current', [TEAM_STATE.REVIEW]: 'Needs review', [TEAM_STATE.PREVIOUS]: 'Previous' };

function Badge({ text, color }) {
  return <span style={{ fontSize: 10, fontWeight: 700, color, background: `${color}18`, border: `1px solid ${color}55`, borderRadius: 5, padding: '1px 6px', whiteSpace: 'nowrap' }}>{text}</span>;
}
function PersonRow({ m, season }) {
  const cat = season === 'all' ? resolveInstructorCategory(m) : resolveInstructorCategory(m, { season });
  const caps = getCapabilities(m, season === 'all' ? undefined : season);
  const seasons = Object.keys(m.seasonMemberships || {}).sort();
  return (
    <div data-testid={`team-row-${m.userId}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderTop: `1px solid ${V.border}`, fontSize: 12.5 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: V.t1 }}>{m.name || m.userId}{m.testAccount && <span style={{ marginLeft: 6, fontSize: 9, color: V.amber, border: `1px solid ${V.amber}`, borderRadius: 4, padding: '0 4px' }}>TEST</span>}</div>
        {season === 'all'
          ? <div style={{ fontSize: 10.5, color: V.t3, marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>{seasons.map((s) => <span key={s}>{s.replace('_', '/')} · {catLabel(resolveInstructorCategory(m, { season: s }))}</span>)}</div>
          : <div style={{ fontSize: 10.5, color: V.t3, marginTop: 2 }}>{caps.length ? `Capabilities: ${caps.join(', ')}` : (m.jobFunction ? m.jobFunction : '—')}</div>}
      </div>
      <Badge text={catLabel(cat)} color={catColor(cat)} />
      {m.jobFunction === 'driver' && <Badge text="driver" color={V.t2} />}
      {season === '2026_27' && <Badge text={stateLabel[resolveCurrentTeamState(m)]} color={resolveCurrentTeamState(m) === 'confirmed_current' ? V.green : V.amber} />}
    </div>
  );
}
function Group({ title, list, season }) {
  if (!list.length) return null;
  return (
    <div style={{ marginBottom: 14, background: V.card, border: `1px solid ${V.border}`, borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '7px 12px', background: V.surface, fontWeight: 700, fontSize: 12, color: V.t1, display: 'flex', justifyContent: 'space-between' }}><span>{title}</span><span style={{ color: V.t3 }}>{list.length}</span></div>
      {list.map((m) => <PersonRow key={m.userId} m={m} season={season} />)}
    </div>
  );
}

// Pass 58E — the production-facing Team must not show obvious demo/test humans (e.g. "TEST Instructor").
// Exclude by the explicit testAccount flag OR a "test" name token. Real staff are never named this way.
const isDemoTestMember = (m = {}) => m.testAccount === true || /\btest\b/i.test(String(m.name || m.displayName || ''));

export default function OrgTeamSeasons({ members = [], bookings = [] }) {
  const [season, setSeason] = useState('2026_27');
  const realMembers = useMemo(() => members.filter((m) => !isDemoTestMember(m)), [members]);
  const instructorMembers = useMemo(() => realMembers.filter(isOperationalStaff), [realMembers]);

  const view = useMemo(() => {
    if (season === 'all') {
      // one card per canonical person (dedup by userId)
      const byUser = new Map();
      for (const m of instructorMembers) if (!byUser.has(m.userId)) byUser.set(m.userId, m);
      return { all: [...byUser.values()].sort((a, b) => (a.name || '').localeCompare(b.name || '')) };
    }
    const roster = getMembersForSeason(realMembers, bookings, season);
    // Pass 49A §12: for the CURRENT season, the operating team = CONFIRMED current only;
    // unresolved people are shown collapsibly as "needs roster review", not as the team.
    const isCurrent = season === '2026_27';
    const confirmed = isCurrent ? roster.filter((m) => resolveCurrentTeamState(m) === TEAM_STATE.CURRENT) : roster;
    const review = isCurrent ? roster.filter((m) => resolveCurrentTeamState(m) === TEAM_STATE.REVIEW) : [];
    const { teaching, other } = splitTeachingStaff(confirmed);
    const school = teaching.filter((m) => resolveInstructorCategory(m, { season }) === 'school');
    const collective = teaching.filter((m) => resolveInstructorCategory(m, { season }) === 'collective');
    const unclassified = teaching.filter((m) => resolveInstructorCategory(m, { season }) === 'unclassified');
    return { school, collective, unclassified, other, review, confirmedTotal: confirmed.length, total: roster.length, isCurrent };
    // `instructorMembers` is derived from `members` (already a dep); listing it too would not change the result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, bookings, season]);
  const [showReview, setShowReview] = useState(false);

  return (
    <div style={{ fontFamily: F }}>
      <div style={{ display: 'flex', gap: 2, background: V.surface, border: `1px solid ${V.border}`, borderRadius: 8, padding: 2, marginBottom: 14, width: 'fit-content' }}>
        {SEASONS.map(([k, l]) => (
          <button key={k} onClick={() => setSeason(k)} data-testid={`team-season-${k}`} style={{ fontFamily: F, fontSize: 12.5, fontWeight: 700, padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: season === k ? V.card : 'transparent', color: season === k ? V.t1 : V.t3 }}>{l}</button>
        ))}
      </div>
      {season === 'all'
        ? <Group title="All people (one per person · season history)" list={view.all} season="all" />
        : <>
            <div style={{ fontSize: 12, color: V.t2, marginBottom: 8 }}>
              {view.isCurrent
                ? <><strong>SAS Team — {view.school.length + view.unclassified.length + view.other.length} current for the 2026/27 season</strong>: {view.school.length} instructors · {view.unclassified.length + view.other.length} operations.{view.collective.length ? ` Collective (${view.collective.length}) is a separate source, not currently connected.` : ''}</>
                : <>{season.replace('_', '/')} roster — {view.total} people. School/Collective is this season's affiliation; capability is separate.</>}
            </div>
            <Group title={view.isCurrent ? 'SAS instructors' : 'SAS School'} list={view.school} season={season} />
            <Group title={view.isCurrent ? 'Collective relationship · schedule source not connected' : 'Collective'} list={view.collective} season={season} />
            <Group title={view.isCurrent ? 'Current · category unresolved' : 'Unclassified'} list={view.unclassified} season={season} />
            <Group title="Other / non-teaching staff" list={view.other} season={season} />
            {view.isCurrent && view.review.length > 0 && (
              <div style={{ marginBottom: 14, background: V.card, border: `1px dashed ${V.amber}`, borderRadius: 10, overflow: 'hidden' }}>
                <button onClick={() => setShowReview((s) => !s)} data-testid="needs-review-toggle" style={{ width: '100%', textAlign: 'left', padding: '8px 12px', background: '#FEF9EC', fontWeight: 700, fontSize: 12, color: V.amber, border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{showReview ? '▾' : '▸'} Needs roster review (returning-eligible, unconfirmed)</span><span>{view.review.length}</span>
                </button>
                {showReview && view.review.map((m) => <PersonRow key={m.userId} m={m} season={season} />)}
              </div>
            )}
            {view.total === 0 && <div style={{ padding: 16, color: V.t3, fontSize: 12 }}>No people for {season.replace('_', '/')}.</div>}
          </>}
    </div>
  );
}
